import { useCallback } from "react";
import { Outlet, useNavigate, useSearchParams } from "react-router-dom";
import { captureException, setUser } from "@sentry/react";
import { Auth0ProviderProps, AuthProvider } from "@jana-technology/platform-ui";

import { auth0Client } from "auth/auth0-client";
import { DEFAULT_ROUTE, LOGIN_ERROR_ROUTE } from "constants/routing";

export const App = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const onRedirectCallback = useCallback<
    NonNullable<Auth0ProviderProps["onRedirectCallback"]>
  >(
    (appState, user) => {
      const {
        name,
        email,
        email_verified,
        preferred_username,
        locale,
        sub,
        gender,
      } = user ?? {};

      // set user in sentry to make debugging more user centric
      setUser({
        name,
        email,
        email_verified,
        username: preferred_username,
        id: sub,
        locale,
        gender,
      });

      // search params are preserved from the initial visit via location.state
      const [path, search] = (appState?.returnTo || DEFAULT_ROUTE).split("?");

      setSearchParams(new URLSearchParams(search));
      navigate(path);
    },
    [navigate, setSearchParams],
  );

  const onErrorCallback = useCallback<
    NonNullable<Auth0ProviderProps["onErrorCallback"]>
  >(
    (error) => {
      captureException(error, {
        tags: {
          component: "App",
          caller: "onErrorCallback",
        },
      });

      navigate(`${LOGIN_ERROR_ROUTE}?${searchParams.toString()}`);
    },
    [navigate, searchParams],
  );

  return (
    <AuthProvider
      auth0Client={auth0Client}
      onRedirectCallback={onRedirectCallback}
      onErrorCallback={onErrorCallback}
    >
      <Outlet />
    </AuthProvider>
  );
};

export default App;
